// Frontend mirror of src/services/permission-catalog.js NODES. Keep in
// sync manually — adding a menu item ALWAYS requires the matching node
// here and in the backend catalog.
//
// The admin UI fetches the catalog from /api/v1/permissions/catalog
// (single source of truth), so this file is only used for:
//   - typing / IntelliSense for node paths used in <PermissionGate>
//   - the optional offline tree-build helper at the bottom
// We DON'T import the backend file here because that would couple the
// frontend bundle to Node-specific code. Mirror it.

export type PermLevel = 'hidden' | 'read' | 'write';

export type PermNode = {
  path: string;
  label: string;
  parent?: string;
};

export const NODES: PermNode[] = [
  { path: 'dashboard',   label: 'Dashboard' },
  { path: 'home',        label: 'Home' },
  { path: 'bi',          label: 'Analytics' },
  { path: 'receivables', label: 'Receivables' },
  { path: 'payables',    label: 'Payables' },
  { path: 'sales',       label: 'Sales' },
  { path: 'projects',    label: 'Projects' },
  { path: 'products',    label: 'Products' },
  { path: 'production',  label: 'Production' },
  { path: 'inventory',   label: 'Inventory' },
  { path: 'wms',         label: 'Warehouse Management' },
  { path: 'ledger',      label: 'Ledger' },
  { path: 'admin',       label: 'Admin Center' },

  { path: 'receivables.invoices',     label: 'Invoices',     parent: 'receivables' },
  { path: 'receivables.credit_notes', label: 'Credit Notes', parent: 'receivables' },
  { path: 'receivables.receipts',     label: 'Receipts',     parent: 'receivables' },
  { path: 'receivables.contracts',    label: 'Contracts',    parent: 'receivables' },
  { path: 'receivables.ar_aging',   label: 'AR Aging',     parent: 'receivables' },
  { path: 'receivables.statements', label: 'Statements',   parent: 'receivables' },
  { path: 'receivables.reports',    label: 'Reports',      parent: 'receivables' },
  { path: 'receivables.settings',   label: 'Settings',     parent: 'receivables' },
  { path: 'receivables.quickbooks', label: 'QuickBooks Transfers', parent: 'receivables' },

  { path: 'payables.vendors',         label: 'Vendors',         parent: 'payables' },
  { path: 'payables.purchase_orders', label: 'Purchase Orders', parent: 'payables' },
  { path: 'payables.vendor_price_lists', label: 'Vendor Price Lists', parent: 'payables' },
  { path: 'payables.bills',           label: 'Bills',           parent: 'payables' },
  { path: 'payables.payments',        label: 'Payments',        parent: 'payables' },
  { path: 'payables.bank_files',      label: 'Bank Files',      parent: 'payables' },
  { path: 'payables.ap_aging',        label: 'AP Aging',        parent: 'payables' },
  { path: 'payables.tax_reports',     label: '1099 / Tax Reports', parent: 'payables' },

  { path: 'sales.customers',   label: 'Customers',     parent: 'sales' },
  { path: 'sales.orders',      label: 'Orders',        parent: 'sales' },
  { path: 'sales.shipments',   label: 'Shipments',     parent: 'sales' },
  { path: 'sales.agents',      label: 'Sales Reps',        parent: 'sales' },
  { path: 'sales.leads',       label: 'Leads',         parent: 'sales' },
  { path: 'sales.opportunities', label: 'Opportunities', parent: 'sales' },
  { path: 'sales.price_lists', label: 'Price Lists',   parent: 'sales' },
  { path: 'sales.forecasts',   label: 'Forecasts',     parent: 'sales' },
  { path: 'sales.customer_statuses', label: 'Customer Statuses', parent: 'sales' },
  { path: 'sales.business_types',    label: 'Business Types',    parent: 'sales' },

  { path: 'projects.projects',   label: 'Projects',      parent: 'projects' },
  { path: 'projects.time',       label: 'Time Tracking', parent: 'projects' },
  { path: 'projects.activities', label: 'Activities',    parent: 'projects' },

  { path: 'products.items',           label: 'Items',            parent: 'products' },
  { path: 'products.families',        label: 'Categories',         parent: 'products' },
  { path: 'products.uoms',            label: 'Units of Measure', parent: 'products' },
  { path: 'products.uom_conversions', label: 'Conversions',      parent: 'products' },

  { path: 'production.manufacturing_orders', label: 'Manufacturing Orders', parent: 'production' },
  { path: 'production.report_production',    label: 'Report Production',    parent: 'production' },
  { path: 'production.schedule',             label: 'Schedule',             parent: 'production' },
  { path: 'production.gantt',                label: 'Gantt',                parent: 'production' },
  { path: 'production.capacity_plan',        label: 'Capacity Plan',        parent: 'production' },
  { path: 'production.mrp_runs',             label: 'MRP Runs',             parent: 'production' },
  { path: 'production.statistics',           label: 'Statistics',           parent: 'production' },
  { path: 'production.settings',             label: 'Master Data',          parent: 'production' },
  { path: 'production.settings.boms',                label: 'BOMs',                parent: 'production.settings' },
  { path: 'production.settings.routings',            label: 'Routings',            parent: 'production.settings' },
  { path: 'production.settings.work_centers',        label: 'Work Centers',        parent: 'production.settings' },
  { path: 'production.settings.work_center_groups',  label: 'Work Center Groups',  parent: 'production.settings' },

  { path: 'inventory.on_hand',      label: 'On Hand',           parent: 'inventory' },
  { path: 'inventory.transactions', label: 'Transactions',      parent: 'inventory' },
  { path: 'inventory.transfers',    label: 'Warehouse Transfers', parent: 'inventory' },
  { path: 'inventory.shipments',    label: 'Customer Shipments', parent: 'inventory' },
  { path: 'inventory.packs',        label: 'Packs',              parent: 'inventory' },
  { path: 'inventory.statuses',     label: 'Statuses',           parent: 'inventory' },
  { path: 'inventory.lots',         label: 'Lots',              parent: 'inventory' },
  { path: 'inventory.serials',      label: 'Serials',           parent: 'inventory' },
  { path: 'inventory.warehouses',   label: 'Warehouses',        parent: 'inventory' },
  { path: 'inventory.counts',       label: 'Inventory counts',  parent: 'inventory' },

  { path: 'wms.asn',         label: 'ASN',            parent: 'wms' },
  { path: 'wms.dock',        label: 'Dock Schedule',  parent: 'wms' },
  { path: 'wms.grn',         label: 'Goods Receipts', parent: 'wms' },
  { path: 'wms.qc',          label: 'QC / Quarantine', parent: 'wms' },
  { path: 'wms.putaway',     label: 'Putaway',        parent: 'wms' },
  { path: 'wms.lp',          label: 'License Plates', parent: 'wms' },
  { path: 'wms.bins',        label: 'Bins',        parent: 'wms' },
  { path: 'wms.waves',       label: 'Waves',       parent: 'wms' },
  { path: 'wms.tasks',       label: 'Tasks',       parent: 'wms' },
  { path: 'wms.allocations', label: 'Allocations', parent: 'wms' },
  { path: 'wms.pick',        label: 'Pick',        parent: 'wms' },
  { path: 'wms.pack',        label: 'Pack',        parent: 'wms' },
  { path: 'wms.ship',        label: 'Ship',        parent: 'wms' },

  { path: 'ledger.accounts',      label: 'Chart of Accounts', parent: 'ledger' },
  { path: 'ledger.journal',       label: 'Journal Entries',   parent: 'ledger' },
  { path: 'ledger.trial_balance', label: 'Trial Balance',     parent: 'ledger' },
  { path: 'ledger.profit_loss',   label: 'Profit & Loss',     parent: 'ledger' },
  { path: 'ledger.balance_sheet', label: 'Balance Sheet',     parent: 'ledger' },
  { path: 'ledger.settings',      label: 'Settings',          parent: 'ledger' },
  { path: 'ledger.payment_methods', label: 'Payment Methods', parent: 'ledger' },

  { path: 'admin.company',            label: 'Current Company',     parent: 'admin' },
  { path: 'admin.users',              label: 'Users',               parent: 'admin' },
  { path: 'admin.document_numbering', label: 'Document Numbering',  parent: 'admin' },
  { path: 'admin.document_templates', label: 'Document Templates',  parent: 'admin' },
  { path: 'admin.custom_fields',      label: 'Custom Fields',       parent: 'admin' },
  { path: 'admin.customization',      label: 'Customization Platform', parent: 'admin' },
  { path: 'admin.procedures',         label: 'Procedures',          parent: 'admin' },
  { path: 'admin.webhooks',           label: 'Webhooks',            parent: 'admin' },
  { path: 'admin.integrations',       label: 'Integrations',        parent: 'admin' },
  { path: 'admin.roles',              label: 'Roles & Permissions', parent: 'admin' },
  { path: 'admin.branches',           label: 'Branches',            parent: 'admin' },
  { path: 'admin.new_company',        label: 'Manage Companies',    parent: 'admin' },

  { path: 'service',             label: 'Service' },
  { path: 'service.jobs',        label: 'Service Jobs',       parent: 'service' },
  { path: 'service.assets',      label: 'Service Assets',     parent: 'service' },
  { path: 'service.rmas',        label: 'RMAs',               parent: 'service' },
  { path: 'service.contracts',   label: 'Service Contracts',  parent: 'service' },

  // HR self-service portal (Wave C). Mirrors src/services/permission-catalog.js.
  // Record scope (own / report-subtree) is enforced in the backend handlers;
  // these nodes drive portal nav/feature visibility (granted to the
  // employee/manager roles) and the manager-surface gate (portal.team).
  { path: 'hr',              label: 'HR' },
  { path: 'portal',          label: 'Self-Service Portal',  parent: 'hr' },
  { path: 'portal.profile',  label: 'My Profile',           parent: 'portal' },
  { path: 'portal.pay',      label: 'My Pay (stubs / W-2)', parent: 'portal' },
  { path: 'portal.benefits', label: 'My Benefits',          parent: 'portal' },
  { path: 'portal.team',     label: 'My Team (manager)',    parent: 'portal' },
];

const NODE_PATHS = new Set(NODES.map((n) => n.path));
export function isKnownNode(path: string): boolean {
  return NODE_PATHS.has(path);
}
